<template>
  <v-tab-item
    class="lots"
    data-qa="item-tab-lots">
    <EmptyBlock
      v-if="!groups || !groups.length"
      custom-class="mt-0 pa-10 no-lots"
      data-qa="lots-noData"
      :text="$t('Tenders.NoData')">
      <template #append>
        <div
          v-if="in_creating"
          class="mt-5">
          <sButton
            color="var(--secondary__red)"
            data-qa="btn-add-lots"
            @click="() => returnToStep('AddLots')">
            {{ $t('Tenders.ToAddLots') }}
          </sButton>
        </div>
      </template>
    </EmptyBlock>
    <Groups
      v-else
      can_expand
      :filter-empty-by="gr => gr.count || !active_infinity"
      filter_empty
      :groups="groups"
      :small="isGroupLotsEnabled"
      :with_title="groups.some(group => group.winners)">
      <template #content="{ group }">
        <LotsTabGroupTable
          v-if="group.subGroups && group.subGroups.length"
          :active_infinity="active_infinity"
          :additional_fields="additional_fields"
          :bidding_type="bidding_type"
          :custom-sort-slice="customSortSlice"
          :disable-open-offers-for-lot="disableOpenOffersForLot"
          :group="group"
          :in_creating="in_creating"
          :lots_headers="lots_headers"
          :tender="tender"/>
        <LotsTabTable
          v-else
          :additional_fields="additional_fields"
          :bidding_type="bidding_type"
          :custom-sort-slice="customSortSlice"
          :disable-open-offers-for-lot="disableOpenOffersForLot"
          :group="group"
          :in_creating="in_creating"
          :lots_headers="lots_headers"
          :tender="tender"/>
      </template>
    </Groups>
    <InfinityUpdater
      ref="infinity"
      :action="infinityLoad"
      :active="active_infinity && tab && tab.value == 'lots'"
      call-on-mount
      :pixels_when_update="200"
      selector=" #content-container "/>
  </v-tab-item>
</template>

<script>
import Groups from 'components/common/blocks/groups/Groups.vue'
import EmptyBlock from 'components/common/elements/EmptyBlock.vue'
import InfinityUpdater from 'components/extends/InfinityUpdater.vue'

import LotsTabTable from 'components/tenders/tender page/tender tabs/tables/LotsTabTable.vue'
import LotsTabGroupTable from 'components/tenders/tender page/tender tabs/tables/LotsTabGroupTable.vue'

import { mapGetters } from 'vuex'
import { additionalFieldsMixin, measureMixin, tenderMixin, modalMixin } from 'mixins'

export default {
  name: 'lotsTab',
  mixins: [tenderMixin, measureMixin, additionalFieldsMixin, modalMixin],
  data() {
    return {
      groups: [],
      count: 0,
      sliced_groups: [],
    }
  },
  props: {
    tender: Object,
    in_creating: Boolean,
    returnToStep: Function,
    tab: {
      type: Object
    },
  },
  mounted() {
    this.infinityLoad()
  },
  computed: {
    ...mapGetters({
      tendersSettings: 'getTendersSettings',
    }),
    isGroupLotsEnabled() {
      return this.$can('show', 'useGroupLots') || !!this.tender?.lotGroups?.length
    },
    all() {
      let lots =  this.$_.cloneDeep(this.tender?.lots),
        mapped = this.$_.map(lots ,lot => {
          return {...lot, selectedBid: this.$_.find( this.bids, el => el.lotId == lot.lotId && el.selectedBid )}
        } )
      return mapped.sort((a,b) => {
        let sel = (!!b.selectedBid) - (!!a.selectedBid)
        return sel || a.lotNumber - b.lotNumber
      })
    },
    bids() {
      let bids = []
      this.$_.forEach(this.tender?.bids, item => { bids = bids.concat(this.getActiveDraftBids(item.bidDetails)) })
      return bids
    },
    per_page() {
      return Math.round(document.documentElement.clientHeight / 55)
    },
    active_infinity() {
      return this.all.length > this.count
    },
    disableOpenOffersForLot() {
      if (this.tender?.tenderStatus?.id === this.statuseKeys.REGISTRY || !this.tender?.bids.length || !this.tender?.tenderStatus) { return true }
      const bid = this.tender?.bids.filter(item => !!this.getActiveDraftBids(item.bidDetails))

      return !bid?.length
    },
    bidding_type() {
      return this.tender?.biddingType
    },
    lots_headers() {
      let without_price = !this.bidding_type?.initialRateAvailable || (!this.$can('set', 'BudgetType', 'AMOUNT') && !this.$can('set', 'BudgetType', 'AMOUNT_AND_MINIMAL_STEP')),
        without_address = !this.$can('set', 'deliveryAddress'),
        without_step =  !this.bidding_type?.bidStepAvailable || ( !this.$can('set', 'BudgetType', 'MINIMAL_STEP') && !this.$can('set', 'BudgetType', 'AMOUNT_AND_MINIMAL_STEP')),
        status = this.tender.tenderStatus?.id,
        is_multistaged = this.bidding_type?.isMultistage
          && status <= this.statuseKeys.PREQUALIFICATION && status != this.statuseKeys.BID_ANALYSIS && !this.tender.auctionPeriod ,
        headers = [
          { text: '',
            value: '',
            width: '20px',
            sortable: false,
            order: 10
          },
          { text: this.$t('Catalog.Position'),
            value: 'title',
            order: 20
          },
          { text: this.$t('Tenders.AdditionalDescription'),
            value: 'description',
            width: '20%',
            order: 30
          },
          { text: this.$t('Catalog.Amount'),
            value: 'quantity',
            width: '8%',
            order: 40
          },
          { text: this.bidding_type?.tenderingForPercentage ? this.$t('Tenders.StartingBid') : this.$t('Tenders.StartingPrice'),
            value: 'initialAmount',
            width: '8%',
            order: 50
          },
          { text: this.bidding_type?.tenderingForPercentage ? this.$t('Tenders.IncreaseStep') : this.$t('Tenders.StepRate'),
            value: 'bidStep',
            width: '8%',
            order: 60
          },
          { text: this.$t('Catalog.DeliveryAddress'),
            value: 'addressId',
            width: '8%',
            order: 70
          },
          {
            text: this.$t('Tenders.MinimalQuantity'),
            value: 'minimalSubmitQuantity',
            width: '110px',
            order: 75
          },
          {
            text: this.$t('Catalog.Price'),
            value: 'discounts',
            width: '225px',
            order: 80
          },
          {
            text: this.$t('Tenders.Price'),
            value: 'summ',
            width: this.bidding_type?.tenderingForPercentage ? '0%' : '8%',
            align: this.bidding_type?.tenderingForPercentage ? ' d-none' : '',
            order: 85
          },
        ]
      if (status == 1) {
        headers.pop()
      }

      if (this.bidding_type?.hasLimitBidPrice) {
        headers.splice(5, 0,
          { text: this.$t('Tenders.MinPrice'),
            value: 'bidPriceLimit',
            width: '8%',
            order: 53
          },
        )
      }

      if (without_price || is_multistaged) {
        headers = headers.filter(el => el.value != 'initialAmount' && el.value != 'summ')
      }

      if (without_address) {
        headers = headers.filter(el => el.value != 'addressId')
      }

      if (without_step || is_multistaged) {
        headers = headers.filter(el => el.value != 'bidStep')
      }

      if (!this.bidding_type?.minimalQuantity) {
        headers = headers.filter(el => el.value != 'minimalSubmitQuantity')
      }

      if (this.bidding_type?.saleWithDiscount) {
        headers = headers.filter(el => el.value != 'initialAmount')
      } else {
        headers = headers.filter(el => el.value != 'discounts')
      }

      if (this.additional_fields?.length) {
        headers = this.$_addAdditionalFieldsToHeaders(headers, this.additional_fields)
      }
      headers.push(
        { text: '', value: 'bidders', width: '30px', sortable: false, order: 100000 }
      )
      return headers.sort((a, b) => a.order - b.order)
    },
    additional_fields() {
      let fields = this.tender.additional_fields || []

      return fields
        .filter(f => f.bindingLevel == 'LOT')
    },
  },
  methods: {
    getActiveDraftBids(bidDetails) {
      return this.$_.filter(bidDetails, bid => ['ACTIVE', 'DRAFT'].includes(bid.bidStatus))
    },
    setGroups() {
      let that = this,
        all = that?.all,
        groups = []
      if (this.in_creating) {
        groups = this.getGroups(all)
      } else {
        groups = this.$_getGroupsByWinners(all,
          i => !!i.selectedBid,
          (item) => ({...item, title: item.winners ? this.$t('Tenders.SelectedWinner') : this.bigFirstLetter(this.$t('Tenders.WithoutWinner') ) }) )
      }

      if (this.isGroupLotsEnabled && this.tender?.lotGroups?.length) {
        groups = this.createNestedGroupsWithLotGroups(groups)
      } else {
        groups = this.$_.filter(groups, gr => gr.items && gr.items.length)
      }

      this.groups = groups
      this.expanded = this.$_.cloneDeep( this.groups )
    },
    /**
     * Создает вложенную структуру групп: Победители/Другие участники -> Группа лотов -> лоты
     * @param {Array} winnerGroups - группы по победителям (победители/другие участники)
     * @returns {Array} - группы с вложенными группами лотов
     */
    createNestedGroupsWithLotGroups(winnerGroups) {
      const lotGroups = this.tender.lotGroups || []
      const nestedGroups = []

      winnerGroups.forEach(winnerGroup => {
        const nestedWinnerGroup = {
          ...winnerGroup,
          items: [],
          subGroups: []
        }

        lotGroups.forEach(lotGroup => {
          const lotsInGroup = winnerGroup.items.filter(lot => 
            lotGroup.innerLotIds && lotGroup.innerLotIds.includes(lot.lotId)
          )

          if (lotsInGroup?.length) {
            nestedWinnerGroup.subGroups.push({
              id: `${winnerGroup.id}_lotGroup_${lotGroup.id}`,
              title: lotGroup.title,
              description: lotGroup.description,
              items: lotsInGroup ?? [],
              count: lotsInGroup.length,
              isLotGroup: true
            })
          }
        })

        const ungroupedLots = winnerGroup.items.filter(lot => 
          !lotGroups.some(lotGroup => 
            lotGroup.innerLotIds && lotGroup.innerLotIds.includes(lot.lotId)
          )
        )

        if (ungroupedLots.length > 0) {
          nestedWinnerGroup.subGroups.push({
            id: `${winnerGroup.id}_ungrouped`,
            title: this.$t('Tenders.UngroupedLots'),
            items: ungroupedLots,
            count: ungroupedLots.length,
            isLotGroup: true
          })
        }

        if (nestedWinnerGroup.subGroups.length > 0) {
          nestedGroups.push(nestedWinnerGroup)
        }
      })

      return nestedGroups
    },
    infinityLoad() {
      if (this.count < this.all.length) {
        this.count = Math.min(this.all.length, this.count + this.per_page)
        this.setGroups()
        this.updGroups()
      }
    },
    updGroups() {
      let groups = this.groups,
        count = this.count

      groups.forEach(el => {
        if ( !this.expanded?.some(it => it.id == el.id ) ) {
          el.count = 1
        } else if ( count <= 0 ) {
          el.count = 0
        } else {
          el.count = Math.min(el.items.length, count)
          count = count - el.items.length
        }
      })
    },
    /**
     * сортуємо весь список з групи і обрізаємо !!
     *
     * group - об2єкт групи
     * args - масив з  https://vuetifyjs.com/en/api/v-data-table/#props-custom-sort - (items: any[], sortBy: string[], sortDesc: boolean[], locale: string, customSorters?: Record<string, compareFn>) => any[]
     */
    customSortSlice(group , args) {
      let [, [sort_by], [sort_desc]] = args,
        items = this.$_.cloneDeep( group.items ),
        slice = its => its.slice(0, group.count)

      if (!sort_by) { return slice(items) }

      if ( sort_by ) {
        if ( sort_by == 'title' || sort_by == 'description') {

          items = items.sort((a,b) => {
            let  first = a[sort_by], second = b[sort_by]
            if ( sort_by == 'title') {
              first ||= a.lotSubject
              second ||= b.lotSubject
            }

            return (first || '').localeCompare(second || '')
          })
        } else {
          items = this.$_.sortBy(items, [item => {
            let  value = item[sort_by]
            if ( sort_by == 'initialAmount' ) {
              value = item.initialAmount ?? item.startAmount
            } else if ( sort_by == 'bidStep' ) {
              value = item.bidStep ?? item.minimalStepPercent
            } else if ( sort_by == 'summ' ) {
              value = (item.initialAmount || item.startAmount || 0 ) * item.quantity
            }
            return value
          }])
        }

      }
      if ( sort_desc) {
        items = this.$_.reverse(items)
      }

      return slice(items)
    },
  },
  watch: {
    all() {
      this.setGroups()
      this.updGroups() // для обновлення count
    }
  },
  components: {
    Groups,
    EmptyBlock,
    InfinityUpdater,
    LotsTabGroupTable,
    LotsTabTable
  }
}
</script>